// 서버 REST 클라이언트(제어측). 로그인 + 관리 조회(기기목록/주소록/그룹) + 관리 화면(사용자·권한·이력·감사 로그).
// 기존 NestJS API 를 그대로 소비.
// 네이티브 Viewer 의 ServerApiClient 와 동일 계약(설계서 §14). 인증은 accessToken(Bearer) + **401 시 1회 refresh 회전**.
import { ORG_CONTEXT_HEADER, SERVER } from './config';

export interface AgentInfo {
  id: string;
  deviceName: string;
  deviceCode: string;
  online: boolean;
  groupId?: string | null;
  groupName?: string | null;
  /** 관리자가 차단한 기기(접속 자체가 거부됨). online 과 독립된 축이다. */
  disabled?: boolean;
  /** 차단 시각(ISO). 정상이면 null. */
  disabledAt?: string | null;
  /**
   * 익명(기기 코드 + 접속 비밀번호) 접속만 차단됨 — **등록 사용자 접속은 그대로 열려 있다.**
   * 위 `disabled`(기기 전체 킬 스위치)와는 다른 축이다. 둘을 한 값으로 합치면 안 된다.
   */
  anonDisabled?: boolean;
  /** 익명 차단 시각(ISO). 허용 상태면 null. */
  anonDisabledAt?: string | null;
  /**
   * 에이전트 버전 — 서버가 **접속마다** 갱신한다(2026-09-10 이전 서버·에이전트는 등록 당시 값).
   * 관리자가 "어느 기기가 옛 버전인가"(= 자동 업데이트가 안 간 기기)를 식별하는 근거. 모르면 null.
   */
  appVersion?: string | null;
  /**
   * 사람이 이름을 직접 지정한 시각(ISO). null = 기기가 접속할 때 알려 준 이름을 쓰는 중이다.
   *
   * 기기 이름은 평소 접속마다 기기 쪽 이름(호스트 이름)으로 갱신되는데, 여기서 이름을 바꾸면
   * 이 값이 찍히며 **그 이후 기기 보고를 무시**한다. 화면은 이 값으로 "직접 지정한 이름"을 표시한다.
   */
  renamedAt?: string | null;
}

/** 기기 비활성화/재활성화 결과. 부수효과(끊긴 세션·소켓)를 사용자에게 그대로 안내하기 위해 함께 받는다. */
export interface AgentDisableResult {
  agentId: string;
  deviceCode: string;
  disabled: boolean;
  disabledAt: string | null;
  /** 이 조작으로 강제 종료된 진행 중 세션 수. */
  closedSessions: number;
  /** 연결돼 있던 Agent 소켓을 실제로 끊었는지. */
  disconnected: boolean;
}

/**
 * 익명 접속 차단/허용 결과.
 *
 * 기기 전체 차단과 달리 **부수효과가 없다**(끊긴 세션·소켓 필드가 없는 이유) —
 * 진행 중인 세션은 이미 승인을 받은 것이고, 이 스위치는 *새* 익명 요청만 막는다.
 */
export interface AgentAnonDisableResult {
  agentId: string;
  deviceCode: string;
  anonDisabled: boolean;
  anonDisabledAt: string | null;
}

/**
 * 기기 이름 최대 길이(문자). 서버 `AGENT_NAME_MAX_LENGTH`(constants.ts)·에이전트
 * `DEVICE_NAME_MAX_CHARS`(agent_main.rs)와 **같은 값이어야 한다** — 입력칸에서 미리 막아
 * "저장을 눌렀더니 400" 이 되는 일을 없앤다.
 */
export const AGENT_NAME_MAX_LENGTH = 64;

/** 기기 이름 변경 결과 — 서버가 실제로 적용한 값(앞뒤 공백 제거 등 정규화 후)이 온다. */
export interface AgentRenameResult {
  agentId: string;
  deviceCode: string;
  deviceName: string;
  renamedAt: string;
}

/**
 * 기기 그룹(§14.20 — 여러 단계 트리). 서버는 평면 목록을 주고 트리는 `devices/groupTree.ts` 가 조립한다.
 * 개수는 **내가 볼 수 있는 것만**(일반 사용자에게는 서버가 이미 걸러 준다).
 */
export interface GroupInfo {
  id: string;
  /** 상위 그룹. null = 최상위. */
  parentId: string | null;
  name: string;
  /** 이 그룹에 직접 든 관리 기기 수(하위 제외). */
  deviceCount: number;
  /** 이 그룹에 직접 든 공유 ID 항목 수. */
  entryCount: number;
  /** 하위 그룹 수. */
  childCount: number;
  /** 아직 쓸 수 있는 등록 토큰 수(관리자만 — 삭제 확인 안내용). */
  activeTokenCount: number;
}

/**
 * 조직 공유 ID 항목(§14.20) — 그룹 트리의 🔖. 코드 + 이름만(비밀번호는 저장하지 않음 — 접속 때마다 입력).
 * 조회는 구성원 누구나, 변경은 관리자.
 */
export interface OrgEntryInfo {
  id: string;
  groupId: string;
  deviceCode: string;
  alias: string;
  /** 같은 코드가 이 조직의 관리 기기이기도 하면 그 id. */
  managedAgentId: string | null;
  /** 관리 기기일 때만 알 수 있다(그 밖엔 null). */
  online: boolean | null;
}

/** 개인 주소록 항목(트리의 "내 주소록" 가지 — 나만 보인다). */
export interface SavedDeviceInfo {
  id: string;
  deviceCode: string;
  alias: string;
  deviceName?: string | null;
  online?: boolean;
  reachable?: boolean;
}

/** enrollment token 발급 결과(§17.9 C — 기기 조직 편입용). enrollmentToken 은 이 응답에서만 노출. */
export interface EnrollmentTokenResult {
  tokenId: string;
  enrollmentToken: string;
  maxUses: number;
  expiresAt: string;
  /** 이 토큰으로 등록된 기기가 들어갈 그룹(§14.20). */
  groupId?: string;
}

/** 이메일 중복 확인 결과(`POST /api/users/email-check`, 서버 `EmailCheckResult`). */
export interface EmailCheckResult {
  /** 서버가 정규화한 주소(소문자·공백 제거). */
  email: string;
  /** 지금 아무도 쓰지 않는다. */
  available: boolean;
  /** 내가 지금 쓰는 주소다. */
  mine: boolean;
}

/** 내 프로필(`GET/PATCH /api/users/me`). 서버 `UserSummary` 중 화면이 쓰는 것만. */
export interface MyProfile {
  userId: string;
  userName: string;
  /** **로그인 ID** 이자 기기 등록 요청의 대상 주소(§14.19 — 필수). */
  email: string;
  /** DB 의 등급(`su`·`admin`·`user`). su 가 조직에 들어가 있어도 `su` 다. */
  role: string;
  /** 수퍼유저인지(su 화면을 보일지). */
  isSu?: boolean;
}

/** 나에게 온 기기 등록 요청(`GET /api/devices/claim-requests`). */
export interface PendingClaim {
  requestId: string;
  agentId: string;
  deviceCode: string;
  deviceName: string;
  createdAt: string;
  /** 만료 시각 — 지나면 목록에서 사라진다(서버가 제외). */
  expiresAt: string;
}

// ── 수퍼유저(§14.19) — 서버 `/api/su/*` 응답 모델 ─────────────────────────────────────────────

/** 조직 한 줄(서버 `SuOrgRow`). */
export interface SuOrgRow {
  orgId: string;
  orgName: string;
  status: string;
  createdAt: string;
  users: number;
  admins: number;
  devices: number;
  onlineDevices: number;
  sessions7d: number;
  lastSessionAt: string | null;
  /** 조직 동시 세션 상한(§15, 2026-10-23). null = 무제한. 옛 서버는 없다(undefined) → 무제한으로 본다. */
  maxConcurrentSessions?: number | null;
  /** 지금 열린 로그인 세션 수(수퍼유저 제외 — 상한 판정과 같은 기준). 옛 서버는 없다. */
  openSessions?: number;
}

/** 대시보드(서버 `SuDashboard`). */
export interface SuDashboard {
  totals: {
    orgs: number;
    activeOrgs: number;
    users: number;
    devices: number;
    onlineDevices: number;
    onlineViewers: number;
    activeSessions: number;
    sessions24h: number;
    sessions7d: number;
    anonSessions7d: number;
  };
  orgs: SuOrgRow[];
}

/** 서버 상태(서버 `SuServerStatus`) — 보기 전용. */
export interface SuServerStatus {
  serverTime: string;
  startedAt: string;
  uptimeSec: number;
  buildCommit: string | null;
  node: string;
  platform: string;
  memory: { rssBytes: number; heapUsedBytes: number; totalBytes: number; freeBytes: number };
  loadAvg: number[];
  disk: { path: string; totalBytes: number; freeBytes: number } | null;
  db: { migrations: number; lastMigration: string | null; lastMigratedAt: string | null; sizeBytes: number | null };
  online: { devices: number; viewers: number };
  release: { version: string | null; publishedAt: string | null };
}

/** 조직 변경 결과(서버 `SuOrgUpdateResult`). */
export interface SuOrgUpdateResult {
  orgId: string;
  orgName: string;
  status: string;
  /** 조직 동시 세션 상한(§15). null = 무제한. */
  maxConcurrentSessions?: number | null;
  cutOff: { users: number; revokedLogins: number; closedSessions: number };
}

/** 되살리기 결과(서버 `SuOrgRestoreResult`, §14.19.10). */
export interface SuOrgRestoreResult extends SuOrgUpdateResult {
  restoredEmails: number;
  emailConflicts: Array<{ userId: string; userName: string; originalEmail: string }>;
}

/** "삭제된 조직" 한 줄(서버 `SuDeletedOrgRow`, §14.19.10). */
export interface SuDeletedOrgRow {
  orgId: string;
  orgName: string;
  deletedAt: string | null;
  users: number;
}

/**
 * 조직 생성 입력(서버 `CreateOrgDto`) — 첫 관리자 함께.
 * 비밀번호는 보내지 않는다(§14.19.9) — 관리자가 초대 메일 링크로 직접 정한다.
 */
export interface NewOrgInput {
  orgName: string;
  admin: { email: string; userName: string };
}

/**
 * 비밀번호 설정 링크 발급 결과(서버 `PasswordLinkResult`, §14.19.9).
 * `sent=false`(메일 미설정·실패)면 `link` 가 실려 온다 — 화면이 보여 주고 관리자가 직접 전달한다.
 */
export interface PasswordLinkResult {
  purpose: 'invite' | 'reset';
  sent: boolean;
  expiresAt: string;
  link?: string;
}

/** 링크 확인 결과(설정 페이지). */
export interface PasswordLinkInfo {
  purpose: 'invite' | 'reset';
  email: string;
  userName: string;
  expiresAt: string;
}

// ── 관리 화면(2026-09-15) — 서버 `/api/users`·`/api/sessions`·`/api/access-logs` 응답 모델 ─────────────

/** 페이지네이션 응답(서버 `Paged<T>`). `total` 은 필터 적용 후 전체 개수. */
export interface Paged<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

/** 조직 사용자(서버 `UserSummary`). */
export interface UserInfo {
  userId: string;
  /** 로그인 ID(§14.19 — 이메일). */
  email: string;
  userName: string;
  role: string;
  status: string;
  /**
   * 저장된 허용 기능(§14.19.2, `features.ts` 의 값). 관리자·su 는 이 값과 무관하게 **전부**다(화면이 편집을 막는다).
   * 옛 서버(FP1 이전)는 이 필드가 없다 → 없으면 전부로 본다.
   */
  features?: string[];
  /** 비밀번호 설정 대기(§14.19.9 — 초대만 받고 아직 링크로 정하지 않음). 옛 서버는 없다 → 없으면 false. */
  passwordPending?: boolean;
  /**
   * 새 이메일 지정 필요(§14.19.10) — 조직을 되살렸는데 원래 주소를 다른 계정이 써서 되찾지 못했다(비활성).
   * 이때 `email` 은 임시 주소라 화면에 보이지 않고 `originalEmail` 을 보여 준다. 옛 서버는 없다 → false.
   */
  emailReassignRequired?: boolean;
  originalEmail?: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  /** 사용자 동시 세션 상한(§15, 2026-10-23). null = 무제한. 옛 서버는 없다(undefined) → 무제한으로 본다. */
  maxConcurrentSessions?: number | null;
}

/**
 * 사용자 추가 입력(서버 `CreateUserDto`).
 * 비밀번호는 보내지 않는다(§14.19.9) — 서버가 "설정 대기" 로 만들고 초대 메일을 보낸다.
 */
export interface NewUserInput {
  /** 로그인 ID(§14.19 — 이메일, 필수·전역 유일). */
  email: string;
  userName: string;
  role?: string;
  /** 허용 기능(§14.19.2). 생략 = 전부. */
  features?: string[];
}

/**
 * 비활성화·비밀번호 초기화 결과 — 그 사람에게서 **무엇을 끊었는지**를 함께 받는다(화면이 그대로 안내한다).
 */
export interface UserKillResult {
  userId: string;
  email: string;
  status: string;
  /** 종료한 로그인(refresh 토큰) 수 */
  revokedLogins: number;
  /** 강제 종료한 진행 중 원격 세션 수 */
  closedSessions: number;
  /** 열려 있던 뷰어 연결을 끊었는지 */
  disconnected: boolean;
}

export interface UserDeleteResult {
  userId: string;
  email: string;
  deleted: true;
  closedSessions: number;
}

/** 사용자의 기기 접근 권한(서버 `PermissionItem`). */
export interface PermissionInfo {
  agentId: string;
  deviceName: string;
  deviceCode: string;
  permissionType: string;
  createdAt: string;
}

/** 세션(접속 이력) 항목(서버 `SessionListItem`). */
export interface SessionItem {
  sessionId: string;
  status: string;
  approvalStatus: string | null;
  startTime: string | null;
  endTime: string | null;
  agentId: string;
  deviceName: string;
  deviceCode: string;
  /** 익명 접속·삭제된 사용자는 null — 둘은 `anon` 으로 구분한다(표시 이름도 서버가 구분해 준다). */
  viewerUserId: string | null;
  viewerUserName: string;
  anon: boolean;
  clientIp: string | null;
}

/** 감사 로그 항목(서버 `AccessLogItem`). 사용자 관리 이벤트의 user* 는 **조작한 관리자**다. */
export interface AccessLogItem {
  logId: string;
  sessionId: string | null;
  userId: string | null;
  agentId: string | null;
  eventType: string;
  message: string;
  createdAt: string;
  userName: string | null;
  /** 행위자 이메일(= 로그인 ID). */
  userEmail: string | null;
  /** 행위자가 수퍼유저인지(§14.19) — "우리 조직 사람이 아닌 누가 했나" 표시. */
  userIsSu?: boolean;
  deviceName: string | null;
  deviceCode: string | null;
}

/** 쿼리 문자열 — 비어 있는 값(undefined·'')은 보내지 않는다(서버 DTO 검증이 빈 문자열을 거부한다). */
function toQuery(params: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') {
      q.set(k, String(v));
    }
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * 로그인 API 실패 — 화면 문장(`message`)은 종전과 같고, **상태코드**를 함께 싣는다(2026-09-29).
 * 대부분의 호출측은 `(e as Error).message` 만 쓰므로 영향이 없다. 상태로 갈라야 하는 곳만 `instanceof` 로 본다
 * (예: 이메일 [중복 확인] — 옛 서버의 404 는 "확인 불가, 저장 때 판정" 으로 넘긴다).
 */
export class ApiHttpError extends Error {
  constructor(
    message: string,
    /** HTTP 상태코드(404·409·429 …). */
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiHttpError';
  }
}

/**
 * 실패 응답 → 화면에 보일 문장.
 * 서버(Nest)는 `{message}` 에 **한국어 사유**를 담는다("본인 계정은 비활성화할 수 없습니다" 등, 검증 오류는 배열).
 * 종전에는 `PATCH /api/... → 409` 만 보여 사람이 이유를 알 수 없었다(2026-09-15 관리 화면에서 드러남).
 * 상태코드는 괄호로 남긴다 — 문의를 받았을 때 원인을 좁히는 데 쓴다.
 */
async function errorMessage(r: Response, method: string, path: string): Promise<string> {
  const body = (await r.json().catch(() => null)) as { message?: unknown } | null;
  const raw = body?.message;
  const msg = Array.isArray(raw) ? raw.join(', ') : typeof raw === 'string' ? raw : '';
  return msg ? `${msg} (${r.status})` : `${method} ${path} → ${r.status}`;
}

// ── 비밀번호 설정 링크 — 공개(로그인 없이) 경로(§14.19.9) ─────────────────────────────────────

/** 로그인 없이 부르는 POST(JSON). 실패하면 서버 문구로 던진다. */
async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(`${SERVER}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    throw new Error(await errorMessage(r, 'POST', path));
  }
  return (await r.json()) as T;
}

/**
 * 로그인 창 "비밀번호 찾기" — 그 이메일로 재설정 링크를 보낸다.
 * 서버는 계정이 있든 없든 **같은 응답**을 준다(계정 열거 방지) → 화면도 늘 같은 안내를 보인다.
 */
export function requestPasswordReset(email: string): Promise<{ accepted: true }> {
  return publicPost('/api/auth/password/forgot', { email });
}

/** 설정 페이지: 링크가 쓸 수 있는지 + 누구의 무엇인지. 쓸 수 없으면 서버 문구로 던진다. */
export function inspectPasswordLink(token: string): Promise<PasswordLinkInfo> {
  return publicPost('/api/auth/password/inspect', { token });
}

/** 설정 페이지: 링크로 비밀번호를 정한다(1회). */
export function setPasswordByLink(token: string, password: string): Promise<{ email: string; purpose: string }> {
  return publicPost('/api/auth/password/set', { token, password });
}


/** 접근 토큰이 이만큼 안에 만료되면 넘기기 전에 갱신한다(ms) — 세션 창이 소켓을 여는 사이 만료되지 않을 여유. */
export const TOKEN_REFRESH_MARGIN_MS = 60_000;

/**
 * JWT 의 만료 시각(ms). 서명은 검사하지 않는다(서버가 한다) — **갱신할지 판단용**일 뿐이다.
 * 형식이 아니거나 `exp` 가 없으면 null(= 모른다 → 갱신하지 않고 기존 401 재시도 경로에 맡긴다).
 */
export function jwtExpiryMs(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) {
    return null;
  }
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const exp = (JSON.parse(atob(b64)) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * 로그인 후 발급된 토큰을 보관하며 인증 요청을 수행하는 클라이언트.
 *
 * 🔴 **접근 토큰은 15분이다.** 종전에는 갱신이 없어(이 파일 머리말이 "401 refresh 는 추후" 라고 적고 있었다)
 * 창을 15분 넘게 열어 두면 그 뒤 모든 요청이 401 로 떨어지고, **재로그인하라는 안내조차 없었다**
 * (2026-09-14 실기 — 기기 목록이 통째로 비고 `GET /api/agents → 401` 만 붉게 떴다).
 * 서버는 로그인 응답에 **refreshToken 을 이미 주고 있었는데 여기서 버리고 있었다.**
 * ⇒ 401 을 만나면 한 번 갱신하고 원 요청을 재시도한다. 갱신까지 실패하면 `onAuthLost` 로 알린다.
 */
export class Api {
  private accessToken = '';
  /** 회전형 refresh 토큰(서버가 갱신 때마다 새로 준다 — 반드시 최신값을 보관할 것). */
  private refreshTokenValue = '';
  /** 갱신 진행 중인 약속 — 401 이 동시에 여러 건 나도 갱신은 한 번만 한다. */
  private refreshing?: Promise<boolean>;

  /**
   * 자격이 완전히 끊겼을 때(갱신 실패) 한 번 불린다. 화면은 이걸 받아 로그인 화면으로 돌아가야 한다.
   * ⚠️ 네트워크 오류로 갱신하지 못한 경우에는 **부르지 않는다** — 그건 자격 문제가 아니라 통신 문제다.
   */
  onAuthLost?: () => void;

  /**
   * 로그인 유지 토큰(refresh)이 바뀔 때마다 불린다 — 통합앱은 이것으로 **자동 로그인용 보관본**을 최신으로 유지한다
   * (`appShell/savedLogin.ts`). 빈 문자열 = 없어졌다(로그아웃·자격 상실 → 보관본도 지워야 한다).
   * 🔴 회전형이라 반드시 **매번** 알려야 한다 — 옛 토큰으로 되살리면 서버가 재사용(도난)으로 보고 전부 무효화한다.
   */
  onRefreshTokenChanged?: (refreshToken: string) => void;

  private setRefreshToken(v: string): void {
    this.refreshTokenValue = v;
    this.onRefreshTokenChanged?.(v);
  }

  /**
   * 보관해 둔 로그인 유지 토큰으로 **조용히** 로그인을 되살린다(자동 로그인). 성공하면 true.
   * 실패해도 `onAuthLost` 를 부르지 않는다 — 앱을 켰을 뿐인데 "유효 시간이 지났다" 는 안내가 뜨면 안 된다.
   */
  async resume(refreshToken: string): Promise<boolean> {
    this.refreshTokenValue = refreshToken;
    const ok = await this.refresh(true);
    if (!ok) {
      this.accessToken = '';
      this.setRefreshToken(''); // 되살리지 못한 보관본은 지운다(네트워크 오류여도 — 다음 실행 때 로그인 화면이 안전하다)
    }
    return ok;
  }

  get token(): string {
    return this.accessToken;
  }

  get isAuthed(): boolean {
    return this.accessToken.length > 0;
  }

  /**
   * **지금 쓸 수 있는** 접근 토큰 — 만료됐거나 곧 만료되면(`TOKEN_REFRESH_MARGIN_MS`) 먼저 갱신한다.
   *
   * 🔴 2026-09-17 결함: 원격 접속은 `token`(들고 있던 값)을 세션 창에 넘긴다. 이 클라이언트는 **자기 요청이 401 을 받을 때만**
   *    갱신하므로, 로그인 뒤 15분 넘게 다른 요청이 없었으면 만료 토큰이 넘어가 세션 탭이 `Unauthorized` 로 실패했다.
   *    세션 창은 받은 토큰으로 자기 소켓을 여는 별도 창이라 401 재시도를 할 수 없다 → 넘기기 전에 여기서 새것으로 만든다.
   * 갱신에 실패해도 던지지 않는다(자격 상실이면 `onAuthLost` 가 이미 화면을 돌린다) — 있는 값을 돌려준다.
   */
  async freshToken(): Promise<string> {
    const exp = jwtExpiryMs(this.accessToken);
    if (this.refreshTokenValue && exp !== null && exp - Date.now() < TOKEN_REFRESH_MARGIN_MS) {
      await this.refresh();
    }
    return this.accessToken;
  }

  /**
   * 수퍼유저가 고른 조직(§14.19.1). 비어 있으면 머리를 싣지 않는다("전체" 모드 — su 전용 API 만 쓴다).
   * 로그아웃하면 비운다. 일반 사용자는 늘 비어 있다.
   */
  orgContext = '';

  /**
   * 로그인 — **이메일**과 비밀번호(§14.19). 요청 필드명은 서버 호환을 위해 `loginId` 그대로다.
   * 실패하면 서버 문구(없는 이메일·틀린 비밀번호가 같은 문구)를 그대로 던진다.
   */
  async login(email: string, password: string): Promise<void> {
    this.orgContext = '';
    const r = await fetch(`${SERVER}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ loginId: email, password }),
    });
    if (!r.ok) {
      throw new Error(await errorMessage(r, 'POST', '/api/auth/login'));
    }
    const j = (await r.json()) as { accessToken: string; refreshToken?: string };
    this.accessToken = j.accessToken;
    this.setRefreshToken(j.refreshToken ?? '');
  }

  /**
   * 로그아웃. 서버에도 알려 **refresh family 를 폐기**한다 — 이제 refresh 토큰을 들고 있으므로,
   * 지우기만 하고 끝내면 살아 있는 재발급 수단이 남는다.
   * 서버 호출 실패는 무시한다(로컬 자격은 어차피 지운다 — 사용자의 "로그아웃" 이 네트워크 때문에 실패하면 안 된다).
   */
  logout(): void {
    const rt = this.refreshTokenValue;
    this.accessToken = '';
    this.orgContext = '';
    this.setRefreshToken(''); // 자동 로그인 보관본도 지운다
    if (rt) {
      void fetch(`${SERVER}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      }).catch(() => {});
    }
  }

  /**
   * 접근 토큰 재발급(회전). 성공하면 새 access/refresh 를 보관하고 `true`.
   *
   * - **자격 거절**(4xx) = 되살릴 수 없다 → 토큰을 비우고 `onAuthLost`.
   * - **네트워크 오류** = 자격 문제가 아니다 → 토큰을 **그대로 두고** `false`(다음 요청에서 다시 시도된다).
   *   여기서 지워 버리면 잠깐 끊긴 것만으로 사용자를 로그아웃시키게 된다.
   */
  /** @param silent true 면 자격 거절이어도 `onAuthLost` 를 부르지 않는다(자동 로그인 — `resume`). */
  private refresh(silent = false): Promise<boolean> {
    if (this.refreshing) {
      return this.refreshing;
    }
    this.refreshing = (async () => {
      try {
        const r = await fetch(`${SERVER}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.refreshTokenValue }),
        });
        if (!r.ok) {
          this.accessToken = '';
          this.setRefreshToken('');
          if (!silent) this.onAuthLost?.();
          return false;
        }
        const j = (await r.json()) as { accessToken: string; refreshToken: string };
        this.accessToken = j.accessToken;
        this.setRefreshToken(j.refreshToken);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = undefined;
      }
    })();
    return this.refreshing;
  }

  private async authed<T>(path: string, init?: RequestInit): Promise<T> {
    // ⚠️⚠️ 헤더를 **객체 스프레드로 합치지 않는다.** `'Content-Type'` 과 `'content-type'` 은 JS 객체에서
    //    서로 다른 키라 둘 다 살아남고, fetch 는 그것을 `content-type: application/json, application/json`
    //    으로 **이어 붙여** 보낸다. 유효한 media type 이 아니므로 서버가 본문을 파싱하지 못하고
    //    ValidationPipe 가 400 을 준다.
    //    🔴 실제로 **기기 차단 버튼이 이 때문에 한 번도 동작하지 않았다**(2026-09-09 발견 — 호출측이
    //    소문자 `content-type` 을 한 번 더 붙이고 있었다). 타입 검사도 테스트도 잡지 못한다 —
    //    두 키 다 유효한 `HeadersInit` 이기 때문이다.
    //    ⇒ `Headers` 로 합친다. `set` 은 **대소문자 무관 덮어쓰기**라 이 사고가 구조적으로 불가능해진다.
    let r = await this.sendAuthed(path, init);
    // 401 = 접근 토큰 만료(15분)일 가능성이 가장 크다 → **한 번만** 갱신하고 같은 요청을 다시 보낸다.
    // ⚠️ 재시도는 `init.body` 가 문자열일 때만 안전하다. 이 클라이언트는 항상 JSON 문자열을 넘긴다
    //    (스트림 본문을 쓰게 되면 한 번 읽힌 뒤라 재시도가 조용히 빈 본문을 보낸다 — 그때는 여기를 고칠 것).
    if (r.status === 401 && this.refreshTokenValue && (await this.refresh())) {
      r = await this.sendAuthed(path, init);
    }
    if (!r.ok) {
      throw new ApiHttpError(await errorMessage(r, init?.method ?? 'GET', path), r.status);
    }
    return (await r.json()) as T;
  }

  /** 현재 접근 토큰으로 한 번 보낸다(응답을 그대로 돌려준다 — 재시도 판단은 호출측). */
  private sendAuthed(path: string, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${this.accessToken}`);
    if (this.orgContext) {
      headers.set(ORG_CONTEXT_HEADER, this.orgContext); // 수퍼유저가 고른 조직(§14.19.1)
    }
    return fetch(`${SERVER}${path}`, { ...init, headers });
  }

  getAgents(): Promise<AgentInfo[]> {
    // 서버(AgentListItem)는 agentId/status 를 주므로 프런트 모델(id/online)로 정규화한다.
    return this.authed<
      Array<{
        agentId?: string;
        id?: string;
        deviceName: string;
        deviceCode: string;
        status?: string;
        online?: boolean;
        groupId?: string | null;
        groupName?: string | null;
        disabled?: boolean;
        disabledAt?: string | null;
        anonDisabled?: boolean;
        anonDisabledAt?: string | null;
        appVersion?: string | null;
        renamedAt?: string | null;
      }>
    >('/api/agents').then((list) =>
      list.map((a) => ({
        id: a.agentId ?? a.id ?? '',
        deviceName: a.deviceName,
        deviceCode: a.deviceCode,
        online: a.online ?? a.status === 'online',
        groupId: a.groupId ?? null,
        groupName: a.groupName ?? null,
        // 구버전 서버(필드 부재) 대비: disabledAt 이 있으면 그것으로도 판정한다.
        disabled: a.disabled ?? a.disabledAt != null,
        disabledAt: a.disabledAt ?? null,
        anonDisabled: a.anonDisabled ?? a.anonDisabledAt != null,
        anonDisabledAt: a.anonDisabledAt ?? null,
        // 구버전 서버는 필드를 주지 않는다 → null(화면에서 버전 표기를 생략).
        appVersion: a.appVersion ?? null,
        // 구버전 서버는 필드를 주지 않는다 → null(= "기기가 알려 준 이름" 으로 취급).
        renamedAt: a.renamedAt ?? null,
      })),
    );
  }

  /**
   * 기기 이름 변경 — 조직 관리자만 호출 가능(비관리자는 403).
   *
   * 바꾼 이름은 **기기가 다시 접속해도 유지된다**(서버가 `renamedAt` 을 찍어 기기 보고를 무시한다).
   * 서버가 앞뒤 공백을 제거하므로 응답의 `deviceName` 을 진실로 삼는다.
   */
  renameAgent(agentId: string, deviceName: string): Promise<AgentRenameResult> {
    return this.authed<AgentRenameResult>(`/api/agents/${agentId}/name`, {
      method: 'PATCH',
      // content-type 은 authed 가 붙인다(여기서 또 주면 위 주석의 사고가 재현된다).
      body: JSON.stringify({ deviceName }),
    });
  }

  /**
   * 기기 비활성화(차단)/재활성화 — 조직 관리자만 호출 가능(비관리자는 403).
   * 차단하면 해당 기기의 접속이 거부되고 진행 중 세션이 즉시 종료된다.
   */
  setAgentDisabled(agentId: string, disabled: boolean): Promise<AgentDisableResult> {
    return this.authed<AgentDisableResult>(`/api/agents/${agentId}/disabled`, {
      method: 'PATCH',
      // content-type 은 authed 가 붙인다(여기서 또 주면 위 주석의 사고가 재현된다).
      body: JSON.stringify({ disabled }),
    });
  }

  /**
   * 익명 접속만 차단/허용 — 조직 관리자만 호출 가능(비관리자는 403).
   *
   * 기기 전체 차단과 달리 **등록 사용자 접속은 그대로 둔다.** "코드만 알면 아무나 승인창을 띄운다" 는
   * 경로만 닫는 스위치다(기기 코드는 트레이 툴팁에 떠 있는 값이라 비밀이 아니다).
   */
  setAgentAnonDisabled(agentId: string, anonDisabled: boolean): Promise<AgentAnonDisableResult> {
    return this.authed<AgentAnonDisableResult>(`/api/agents/${agentId}/anon-disabled`, {
      method: 'PATCH',
      // content-type 은 authed 가 붙인다(여기서 또 주면 위 주석의 사고가 재현된다).
      body: JSON.stringify({ anonDisabled }),
    });
  }

  /**
   * [조직에서 제거](§14.18) — 조직 관리자만(비관리자 403). 이 조직의 소속·이름·그룹·사용자 권한만 정리하고
   * 이 조직 사용자의 세션만 닫는다. **기기 자체와 다른 조직의 소속은 그대로**다(되돌리려면 등록을 새로 받아야 한다).
   */
  removeAgentFromOrg(agentId: string): Promise<{ agentId: string; closedSessions: number }> {
    return this.authed<{ agentId: string; closedSessions: number }>(`/api/agents/${agentId}/org`, {
      method: 'DELETE',
    });
  }

  getGroups(): Promise<GroupInfo[]> {
    // 서버(GroupItem)는 `groupId` 를 준다 → 프런트 모델(`id`)로 정규화한다(getAgents 와 같은 방식).
    // 🔴 종전에는 그대로 받아 `g.id` 가 undefined 였다 → 기기 화면의 그룹 필터 옵션 값이 전부 비어
    //    **그룹을 골라도 필터가 걸리지 않았다**(2026-09-15 관리 화면 작업 중 발견).
    return this.authed<
      Array<{
        groupId?: string;
        id?: string;
        parentId?: string | null;
        name: string;
        deviceCount?: number;
        entryCount?: number;
        childCount?: number;
        activeTokenCount?: number;
      }>
    >('/api/groups').then((list) =>
      list.map((g) => ({
        id: g.groupId ?? g.id ?? '',
        parentId: g.parentId ?? null,
        name: g.name,
        deviceCount: g.deviceCount ?? 0,
        entryCount: g.entryCount ?? 0,
        childCount: g.childCount ?? 0,
        activeTokenCount: g.activeTokenCount ?? 0,
      })),
    );
  }

  /** 그룹 만들기(관리자). parentId 가 없으면 최상위. 같은 부모 아래 이름 중복 409 · 깊이 초과 400. */
  createGroup(name: string, parentId?: string | null): Promise<unknown> {
    return this.authed<unknown>('/api/groups', {
      method: 'POST',
      body: JSON.stringify(parentId ? { name, parentId } : { name }),
    });
  }

  /** 그룹 이동 — 상위 변경(관리자). null = 최상위로. 자기·자손 아래 409 · 깊이 초과 400. */
  moveGroup(groupId: string, parentId: string | null): Promise<unknown> {
    return this.authed<unknown>(`/api/groups/${groupId}/move`, { method: 'POST', body: JSON.stringify({ parentId }) });
  }

  /** 그룹 이름 변경(관리자). */
  renameGroup(groupId: string, name: string): Promise<unknown> {
    return this.authed<unknown>(`/api/groups/${groupId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
  }

  /** 그룹 삭제(관리자) — 비었을 때만(409). 묶인 등록 토큰은 함께 무효가 된다. */
  deleteGroup(groupId: string): Promise<{ groupId: string; revokedTokens: number }> {
    return this.authed<{ groupId: string; revokedTokens: number }>(`/api/groups/${groupId}`, { method: 'DELETE' });
  }

  /** 기기를 다른 그룹으로(관리자). 그룹 필수(§14.20 — "그룹 없음" 없음). */
  moveDeviceToGroup(agentId: string, groupId: string): Promise<unknown> {
    return this.authed<unknown>('/api/groups/move-device', {
      method: 'POST',
      body: JSON.stringify({ agentId, groupId }),
    });
  }

  /** 조직 공유 ID 항목(구성원 누구나). 옛 서버(경로 없음)는 호출부가 빈 목록으로 다룬다. */
  getOrgEntries(): Promise<OrgEntryInfo[]> {
    return this.authed<
      Array<{
        entryId: string;
        groupId: string;
        deviceCode: string;
        alias: string;
        managedAgentId?: string | null;
        online?: boolean | null;
      }>
    >('/api/org-entries').then((list) =>
      list.map((e) => ({
        id: e.entryId,
        groupId: e.groupId,
        deviceCode: e.deviceCode,
        alias: e.alias,
        managedAgentId: e.managedAgentId ?? null,
        online: e.online ?? null,
      })),
    );
  }

  /** 공유 ID 항목 추가(관리자). 같은 코드 409. */
  createOrgEntry(deviceCode: string, alias: string, groupId: string): Promise<unknown> {
    return this.authed<unknown>('/api/org-entries', {
      method: 'POST',
      body: JSON.stringify({ deviceCode, alias, groupId }),
    });
  }

  /** 공유 ID 항목 이름·그룹 변경(관리자). */
  updateOrgEntry(entryId: string, patch: { alias?: string; groupId?: string }): Promise<unknown> {
    return this.authed<unknown>(`/api/org-entries/${entryId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  /** 공유 ID 항목 삭제(관리자). */
  deleteOrgEntry(entryId: string): Promise<unknown> {
    return this.authed<unknown>(`/api/org-entries/${entryId}`, { method: 'DELETE' });
  }

  /**
   * 개인 주소록. 서버(SavedDeviceItem)는 `savedId` 를 준다 → `id` 로 정규화.
   * 🔴 종전에는 그대로 받아 `it.id` 가 undefined 였다 → **주소록 [삭제]가 `/api/address-book/undefined` 로 가 늘 실패**했다
   *    (2026-10-06 그룹 트리 작업 중 발견 — getGroups 의 2026-09-15 결함과 같은 모양).
   */
  getAddressBook(): Promise<SavedDeviceInfo[]> {
    return this.authed<Array<SavedDeviceInfo & { savedId?: string }>>('/api/address-book').then((list) =>
      list.map((it) => ({ ...it, id: it.savedId ?? it.id })),
    );
  }

  saveDevice(deviceCode: string, alias: string): Promise<SavedDeviceInfo> {
    return this.authed<SavedDeviceInfo>('/api/address-book', {
      method: 'POST',
      body: JSON.stringify({ deviceCode, alias }),
    });
  }

  deleteSavedDevice(savedId: string): Promise<unknown> {
    return this.authed<unknown>(`/api/address-book/${savedId}`, { method: 'DELETE' });
  }

  /**
   * 기기 등록(enrollment) 토큰 발급 — admin 전용(§17.9 C, 통합앱 org 온보딩).
   * 발급된 토큰을 기기의 `RS_AGENT_ENROLLMENT_TOKEN` 으로 설정하면 그 기기가 이 조직에 편입된다.
   */
  issueEnrollmentToken(groupId?: string, maxUses?: number, ttlMinutes?: number): Promise<EnrollmentTokenResult> {
    return this.authed<EnrollmentTokenResult>('/api/orgs/enrollment-tokens', {
      method: 'POST',
      body: JSON.stringify({
        // 그룹 메뉴 [이 그룹에 기기 등록](§14.20) — 이 토큰으로 등록된 기기가 그 그룹으로 들어간다.
        ...(groupId ? { groupId } : {}),
        ...(maxUses ? { maxUses } : {}),
        ...(ttlMinutes ? { ttlMinutes } : {}),
      }),
    });
  }

  /** 내 프로필 — 이메일은 **기기 등록 요청(claim)의 대상 주소**라 화면에 노출·수정한다. */
  getMe(): Promise<MyProfile> {
    return this.authed<MyProfile>('/api/users/me');
  }

  /**
   * 이메일 중복 확인(2026-09-29) — [중복 확인] 버튼. 로그인한 누구나(관리자 사용자 추가 · su 조직 만들기 · 내 이메일 변경).
   * 답은 쓸 수 있는지 + 내 주소인지뿐(어디서 쓰이는지는 서버가 알려 주지 않는다). 형식이 틀리면 400, 연타는 429.
   * 🔴 옛 서버(≤ c64dfa3)에는 이 경로가 없다(404) → 화면은 오류 문구를 보이고, 저장 때 409 가 최종 판정이다.
   */
  checkEmail(email: string): Promise<EmailCheckResult> {
    return this.authed<EmailCheckResult>('/api/users/email-check', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * 내 이메일(= 로그인 ID) 변경 — **지금 비밀번호 필수**(§14.19). 지울 수 없다.
   * 다른 사용자가 쓰는 주소면 409, 비밀번호가 틀리면 403.
   */
  updateMyEmail(email: string, currentPassword: string): Promise<MyProfile> {
    return this.authed<MyProfile>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ email, currentPassword }),
    });
  }

  /**
   * 본인 비밀번호 변경(2026-10-23 — [내 계정]). 지금 비밀번호가 틀리면 403, 같은 값·8자 미만이면 400.
   * 성공하면 서버가 **다른 로그인을 모두 끊고** 이 창용 새 토큰 쌍을 준다 → 바로 바꿔 낀다(회전형 refresh 라 보관본도 갱신).
   * 세션 창은 다음에 토큰이 필요할 때 이 창에 새것을 묻는다(sessions/tokenBroker.ts).
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const j = await this.authed<{ accessToken: string; refreshToken: string }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    this.accessToken = j.accessToken;
    this.setRefreshToken(j.refreshToken);
  }

  // ── 수퍼유저(§14.19) — su 가 아니면 서버가 403 ────────────────────────────────────────

  /** 전체 현황 + 조직별 표. */
  suDashboard(): Promise<SuDashboard> {
    return this.authed<SuDashboard>('/api/su/dashboard');
  }

  /** 조직 목록(조직 선택·조직 화면). */
  suOrgs(): Promise<SuOrgRow[]> {
    return this.authed<SuOrgRow[]>('/api/su/orgs');
  }

  /**
   * 조직 생성 + 첫 관리자. 관리자 이메일이 이미 쓰이면 409(조직도 만들어지지 않는다).
   * 관리자에게 초대 메일이 나간다 — `invite`(메일이 안 나갔으면 `link`).
   */
  suCreateOrg(input: NewOrgInput): Promise<{
    orgId: string;
    orgName: string;
    admin: { userId: string; email: string };
    invite?: PasswordLinkResult;
  }> {
    return this.authed('/api/su/orgs', { method: 'POST', body: JSON.stringify(input) });
  }

  /** 조직 이름 변경·정지·재개·동시 세션 상한(null = 무제한 — §15). */
  suUpdateOrg(
    orgId: string,
    patch: { orgName?: string; disabled?: boolean; maxConcurrentSessions?: number | null },
  ): Promise<SuOrgUpdateResult> {
    return this.authed<SuOrgUpdateResult>(`/api/su/orgs/${orgId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  /**
   * 조직 삭제 = 삭제 표시(§14.19.10 — 되살릴 수 있다). `confirmName` = 사람이 입력한 조직 이름(서버도 대조).
   * 등록된 단말기가 남아 있으면 409(문구에 대수), 이름이 다르면 400.
   */
  suDeleteOrg(orgId: string, confirmName: string): Promise<SuOrgUpdateResult & { deletedAt: string }> {
    return this.authed(`/api/su/orgs/${orgId}`, { method: 'DELETE', body: JSON.stringify({ confirmName }) });
  }

  /** 삭제된 조직 목록(최근 삭제 순). */
  suDeletedOrgs(): Promise<SuDeletedOrgRow[]> {
    return this.authed<SuDeletedOrgRow[]>('/api/su/orgs/deleted');
  }

  /**
   * 삭제된 조직 되살리기 → **정지 상태**(확인 후 [재개]). 원래 이메일을 그사이 다른 계정이 쓰면 그 사용자는
   * 비활성 + "새 이메일 지정 필요" 로 되살아나고 `emailConflicts` 에 담긴다.
   */
  suRestoreOrg(orgId: string): Promise<SuOrgRestoreResult> {
    return this.authed<SuOrgRestoreResult>(`/api/su/orgs/${orgId}/restore`, { method: 'POST' });
  }

  /** 서버 상태(보기 전용). */
  suServer(): Promise<SuServerStatus> {
    return this.authed<SuServerStatus>('/api/su/server');
  }

  /** 나에게 온 기기 등록 요청(수락 대기). 만료된 것은 서버가 걸러 준다. */
  getPendingClaims(): Promise<PendingClaim[]> {
    return this.authed<PendingClaim[]>('/api/devices/claim-requests');
  }

  /**
   * 기기 등록 요청 수락/거부. 수락하면 그 기기가 **내 조직에 배정**되고 나에게 제어 권한이 생긴다.
   * @param groupId 들어갈 그룹(§14.20) — 관리자만 의미가 있다(일반 사용자는 서버가 기본 그룹으로).
   */
  resolveClaim(requestId: string, accept: boolean, groupId?: string): Promise<{ requestId: string; status: string }> {
    return this.authed<{ requestId: string; status: string }>(
      `/api/devices/claim-requests/${requestId}/resolve`,
      { method: 'POST', body: JSON.stringify(accept && groupId ? { accept, groupId } : { accept }) },
    );
  }

  // ── 관리 화면(2026-09-15) ───────────────────────────────────────────────────────────
  // 사용자·권한·감사 로그는 관리자 전용(비관리자는 서버가 403). 접속 이력은 누구나 — 비관리자는 서버가 본인 것만 준다.

  /** 조직 사용자 목록(관리자). */
  listUsers(): Promise<UserInfo[]> {
    return this.authed<UserInfo[]>('/api/users');
  }

  /**
   * 사용자 추가(관리자). 이메일(= 로그인 ID)이 이미 쓰이면(다른 조직 포함) 409.
   * 초대 메일이 나간다 — `invite`(메일이 안 나갔으면 `link` 를 직접 전달).
   */
  createUser(input: NewUserInput): Promise<UserInfo & { invite?: PasswordLinkResult }> {
    return this.authed('/api/users', { method: 'POST', body: JSON.stringify(input) });
  }

  /**
   * 비밀번호 설정 링크 메일(관리자, §14.19.9) — 설정 대기면 초대, 아니면 재설정 링크.
   * 이전에 보낸 링크는 무효가 된다. 비활성 계정은 409.
   */
  sendPasswordLink(userId: string): Promise<PasswordLinkResult> {
    return this.authed<PasswordLinkResult>(`/api/users/${userId}/password-link`, { method: 'POST' });
  }

  /**
   * 이름·역할·기능 변경(관리자). 역할 변경은 즉시 적용된다. 본인 역할 변경은 409.
   * `features` 는 **전체 목록으로 덮어쓴다**(§14.19.2) — 다음 세션부터 적용.
   */
  /**
   * `email` 은 "새 이메일 지정 필요"(§14.19.10) 사용자에게만 — 그 밖엔 서버가 409.
   * `maxConcurrentSessions` = 동시 세션 상한(§15) — null 이면 무제한, 다음 접속부터 적용.
   */
  updateUser(
    userId: string,
    patch: { userName?: string; role?: string; features?: string[]; email?: string; maxConcurrentSessions?: number | null },
  ): Promise<UserInfo> {
    return this.authed<UserInfo>(`/api/users/${userId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }

  /** 비활성화/재활성화(관리자). 비활성화하면 그 사람의 로그인·창·진행 중 세션이 끊긴다. 본인은 409. */
  setUserDisabled(userId: string, disabled: boolean): Promise<UserKillResult> {
    return this.authed<UserKillResult>(`/api/users/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ disabled }),
    });
  }

  /** 사용자 삭제(관리자). 되돌릴 수 없다. 본인은 409. */
  deleteUser(userId: string): Promise<UserDeleteResult> {
    return this.authed<UserDeleteResult>(`/api/users/${userId}`, { method: 'DELETE' });
  }

  /** 사용자의 기기 권한 목록(관리자). */
  listPermissions(userId: string): Promise<PermissionInfo[]> {
    return this.authed<PermissionInfo[]>(`/api/users/${userId}/permissions`);
  }

  /** 기기 권한 부여·유형 변경(관리자, 서버가 멱등 upsert). */
  grantPermission(userId: string, agentId: string, permissionType: string): Promise<PermissionInfo> {
    return this.authed<PermissionInfo>(`/api/users/${userId}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ agentId, permissionType }),
    });
  }

  /** 기기 권한 회수(관리자). */
  revokePermission(userId: string, agentId: string): Promise<unknown> {
    return this.authed<unknown>(`/api/users/${userId}/permissions/${agentId}`, { method: 'DELETE' });
  }

  /** 접속 이력 — 관리자=조직 전체 / 일반 사용자=본인 것만(서버가 거른다). */
  listSessions(q: { status?: string; agentId?: string; limit?: number; offset?: number }): Promise<Paged<SessionItem>> {
    return this.authed<Paged<SessionItem>>(`/api/sessions${toQuery(q)}`);
  }

  /** 감사 로그(관리자). */
  listAccessLogs(q: {
    eventType?: string;
    userId?: string;
    agentId?: string;
    sessionId?: string;
    limit?: number;
    offset?: number;
  }): Promise<Paged<AccessLogItem>> {
    return this.authed<Paged<AccessLogItem>>(`/api/access-logs${toQuery(q)}`);
  }
}
